import { resolveEmailSender } from "@ando/ui";
import {
  ActionDrawer,
  type ActionDrawerStatus,
  type ActionErrorKind,
  ActionErrorRecovery,
  type ActionFieldValue,
  ActionReceipt,
  ActionStatusBadge,
  ActionUndoBar,
  AndoBrainLoader,
  CALENDAR_PREVIEW_OWNED_FIELDS,
  CalendarCreateEventPreview,
  DOCUMENT_CREATE_PREVIEW_OWNED_FIELDS,
  DocumentCreatePreview,
  DocumentForkPreview,
  EmailSendDraftPreview,
  FIC_PAYMENT_PREVIEW_OWNED_FIELDS,
  FicCreateInvoicePreview,
  FicCreditNotePreview,
  FicPaymentPreview,
  GenericProposalPreview,
  MESSAGE_PREVIEW_OWNED_FIELDS,
  PEC_PREVIEW_OWNED_FIELDS,
  PaneBody,
  PecSendPreview,
  type PreviewControlledCtx,
  QuotationDraftPreview,
  SEPA_PREVIEW_OWNED_FIELDS,
  SdiInvoicePreview,
  SendMessagePreview,
  SepaPaymentPreview,
  emailOwnedFields,
  sanitizeHtml,
  toast,
} from "@ando/ui";
import * as React from "react";

import { FocusedViewLayout } from "@/features/sources";
import { type PendingProposal, useProposalsStore } from "@/features/sources/state/proposalsStore";
import { canonicalActionType } from "../utils/canonicalActionType";
import { getWorkspaceTabDef } from "@/features/workspace/config";
import { refineDocumentSection, refineField } from "@ando/core";
import { Trans, useLingui } from "@lingui/react/macro";
import { useNavigate } from "@tanstack/react-router";
import {
  useEffectiveSignature,
  useSignatures,
} from "../../sales/hooks/useEmailTemplates";
import { useBrandFonts } from "../../settings/hooks/useBrandFonts";
import {
  useLoadPeopleOptions,
  useSearchAttachableDocuments,
  useUploadAttachment,
} from "../hooks/useActionEditorTransports";
import { activeActionApi } from "../hooks/useActiveAction";
import { useConfirmWithUndo } from "../hooks/useConfirmWithUndo";
import { useDocumentCreateDestination } from "../hooks/useDocumentCreateDestination";
import {
  toStyleTemplatePickerProps,
  useDocumentCreateStyleTemplate,
} from "../hooks/useDocumentCreateStyleTemplate";
import { usePayloadSchema } from "../hooks/usePayloadSchema";
import { useSlideOutlineDraft } from "../hooks/useSlideOutlineDraft";
import { useSlidePreviews } from "../hooks/useSlidePreviews";
import type { ActiveAction } from "../state/activeActionStore";
import { renderActionTypeIcon } from "../utils/actionTypeIcon";
import { readSourceValues } from "../utils/gateSourceValues";
import { ActionAiFillBar } from "./ActionAiFillBar";
import { EmailTemplatePicker } from "./EmailTemplatePicker";

/**
 * FocusedActionView — full-screen "detail pane" for a HITL action
 * proposal.
 *
 * Mirrors ``CalendarEventPane`` / ``InboxDetailPane`` chrome via the
 * shared ``FocusedViewLayout``: orange icon badge + uppercase eyebrow +
 * big display title + optional mono subtitle + scrollable body. The
 * inner body is the existing :class:`ActionDrawer` rendered HEADLESS
 * (``hideHeader`` + ``unstyled``) — same state machine, same
 * ``ActionFieldEditor`` (schema-driven), same reasoning/citations panel,
 * same Rifiuta / Salva bozza / Conferma footer. Zero duplication of
 * the proposed→confirming→executing→executed flow.
 *
 * The previous mount point (``ActionFormSurface``) rendered a compact
 * card ABOVE the routed tab content (squashed mini-card in cima alla
 * work area). That layout coexisted with the underlying list, creating
 * two competing focal points. Users explicitly asked for the same
 * "ricevuto-mail → entra in dettaglio" flow they already know from
 * Conversazioni / Calendario — this view delivers exactly that.
 *
 * Lifecycle entry: ``useActionRouter`` writes ``?proposal=<id>`` on the
 * routed hub URL when a new ``action_proposal`` lands AND the user is
 * not already inside a focused detail. ``FocusedActionSurface`` reads
 * that param, looks up the action in ``activeActionStore``, and mounts
 * this view over the ``<Outlet>``.
 *
 * Back chevron clears ``?proposal=`` without removing the action from
 * the store — pending proposals survive nav, the user can re-open the
 * card via the chip in ``ActionFormSurface`` (notification surface).
 */

const DOC_ACTION_TYPES = new Set(["document_edit", "document_fork_edit"]);

/**
 * Irreversible action types gated behind a type-to-confirm ("ELIMINA")
 * before the final execute unlocks (``<TypedConfirmGate>``). Extend as
 * destructive vendor actions land (e.g. SEPA ``initiate_payment``, a
 * delete-row ``db_write``).
 */
const DESTRUCTIVE_ACTION_TYPES = new Set(["delete_record", "delete_event", "delete_item"]);

// `ActionStatusBadge` (atom) owns the label + variant mapping —
// previously duplicated here as `STATUS_BADGE_LABEL` + `STATUS_BADGE_VARIANT`
// constants. Reuse keeps the focused view in lock-step with the inline
// drawer header (May 24, 2026 single-source-of-truth refactor).

/**
 * Optional injection — when the action is ``document_create``, the
 * caller hands us the destination state ALREADY hooked up to TanStack
 * (provider-scoped folder fetch + controlled state). We pass it
 * through to ``DocumentCreatePreview`` so the user-visible picker
 * mutates the same state the caller will spread into modifications at
 * submit time. Decoupled this way so ``pickPreview`` itself stays
 * pure — the hook lives in ``FocusedActionView`` (rules-of-hooks),
 * not here.
 */
interface PickPreviewExtras {
  /**
   * Pre-resolved, localised copy for the ``document_create`` section-refine
   * toasts. Resolved in the consuming component (where the Lingui ``t``
   * macro is in scope) and threaded in because ``pickPreview`` is a plain
   * helper with no macro context of its own. The error variant takes the
   * runtime detail so the macro literal stays static + extractable.
   */
  readonly refineSectionLabels?: {
    readonly failed: string;
    readonly success: string;
    readonly error: (detail: string) => string;
  };
  readonly documentCreateDestination?: ReturnType<typeof useDocumentCreateDestination>;
  /**
   * Per-slide editable outline state for presentation ``document_create``
   * proposals (controlled slides + filename + ``buildModifications``). When
   * absent / ``hasSlideList=false`` the preview falls back to the read-only
   * markdown outline.
   */
  readonly documentCreateSlides?: ReturnType<typeof useSlideOutlineDraft>;
  /**
   * WYSIWYG rendered slide thumbnails for a SANDBOX-authored deck (finished
   * .pptx binary, no editable outline). When present the content section shows
   * the real slides instead of the text summary. Absent for editable decks /
   * non-presentation / non-sandbox proposals.
   */
  readonly documentCreateSlidePreviews?: ReturnType<typeof useSlidePreviews>;
  /**
   * Style / template choice state for ``document_create`` proposals — the
   * "scegli come vuoi che venga creato" picker fused into the card. Drives
   * the ``_style_id`` / ``_ooxml_template_slug`` content meta-keys at
   * execute time. Absent for non-document_create types.
   */
  readonly documentCreateStyleTemplate?: ReturnType<typeof useDocumentCreateStyleTemplate>;
  /**
   * Effective email signature (sanitized) — shown read-only under the body of
   * ``send_email`` envelopes; the BE appends it at send time.
   */
  readonly signatureHtml?: string;
  /**
   * Pool of selectable signatures (multi-signature model) surfaced as the
   * compact dropdown in the signature footer + the resolved active id and the
   * pick callback. Resolved by the host via ``useSignatures`` /
   * ``useEffectiveSignature``.
   */
  readonly signatures?: readonly { id: string; name: string }[];
  readonly activeSignatureId?: string | null;
  readonly onPickSignature?: (id: string) => void;
  /**
   * Active Brand Kit fonts — surfaced in the email body editor's font dropdown
   * (above the built-in generics). Resolved by the host via ``useBrandFonts``.
   */
  readonly brandFonts?: readonly { family: string; fallback: string }[];
  readonly disabled?: boolean;
  readonly onOpenIntegrations?: () => void;
  /** Navigate to the template-upload settings from the picker empty state. */
  readonly onOpenTemplateSettings?: () => void;
  /**
   * Inline AI refiner transport — wired here once and threaded into
   * every preview that hosts a free-text textarea (email body, message
   * body, …). Keeps the API surface uniform: any new preview that opts
   * into the floating bubble just adds ``refineSubmit={refineSubmit}``
   * to its props and inherits the same UX.
   */
  readonly refineSubmit?: (
    input: import("@ando/ui").RefineSubmitInput,
  ) => Promise<import("@ando/ui").RefineSubmitResult>;
  /**
   * OPTIONAL diff-mode transport (Jul 2026) — threaded alongside
   * ``refineSubmit`` into every editable preview so the body's inline AI
   * refiner shows an Apple-style redline before committing. Backward-
   * compatible: previews that receive only ``refineSubmit`` keep replacing
   * the selection in place.
   */
  readonly refineSubmitWithDiff?: import("@ando/ui").RefineSubmitWithDiff;
  /**
   * People-search transport for previews that render
   * ``<RecipientsInput>`` directly (e.g. the email envelope ``A`` /
   * ``Cc`` / ``Ccn`` rows). Same callback the ``ActionDrawer`` forwards
   * to its inner ``<ActionFieldEditor>`` — ref-stable, see
   * ``useActionEditorTransports``.
   */
  readonly loadPeopleOptions?: import("@ando/ui").LoadPeopleOptions;
  /**
   * Attachment upload transport — threaded into the ``send_email`` /
   * ``send_cold_email`` envelope so its shared ``<EmailAttachmentField>``
   * renders INSIDE the compose sheet (same place/style as the inbox reply)
   * instead of as a separate stacked "Allegati" section below.
   */
  readonly uploadAttachment?: import("@ando/ui").UploadAttachment;
  /**
   * Ando-document attachment search transport — the send-email BE resolves
   * ``content_item_id`` chips, so the envelope KEEPS the "Scegli dai documenti
   * Ando" picker (the inbox reply omits it — BE limit).
   */
  readonly searchAttachableDocuments?: import("@ando/ui").SearchAttachableDocuments;
}

function pickPreview(
  action: ActiveAction,
  extras: PickPreviewExtras = {},
): React.ReactNode | ((ctx: PreviewControlledCtx) => React.ReactNode) {
  const proposed = action.proposedFields;
  const executed = action.editedFields;
  // Chat WS frames ship the raw BE enum value ("create_event"); the console
  // wire ships the canonical ("calendar_create_event") — normalize so BOTH
  // route to the dedicated card instead of the generic fallback.
  switch (canonicalActionType(action.actionType)) {
    case "send_email":
    case "email_send_draft":
      // BE-canonical wire is ``send_email`` (see
      // ``ando-agent/src/models/action_models.py::ActionType.SEND_EMAIL``).
      // The legacy ``email_send_draft`` alias is kept for back-compat
      // with any in-flight FE caller until we sweep callers.
      //
      // Returns a render-prop (NOT a static node) so the envelope inputs
      // bind directly to the ActionDrawer's live values + onChange — the
      // stacked ActionFieldEditor is suppressed via ``previewOwnedFields``
      // below to drop the form-on-top / mock-below duplication (May 26,
      // 2026 UX rewrite — see EmailSendDraftPreview docstring).
      return (ctx: PreviewControlledCtx) => (
        <div className="space-y-3">
          <EmailTemplatePicker
            actionType="send_email"
            values={ctx.values}
            onChange={ctx.onChange}
            disabled={ctx.disabled}
          />
          <EmailSendDraftPreview
            proposalId={action.id}
            refineSubmit={extras.refineSubmit}
            refineSubmitWithDiff={extras.refineSubmitWithDiff}
            proposedFields={proposed}
            executedFields={executed}
            schema={ctx.schema}
            values={ctx.values}
            errors={ctx.errors}
            onChange={ctx.onChange}
            disabled={ctx.disabled}
            loadPeopleOptions={extras.loadPeopleOptions}
            uploadAttachment={extras.uploadAttachment}
            searchAttachableDocuments={extras.searchAttachableDocuments}
            brandFonts={extras.brandFonts}
            signatureHtml={extras.signatureHtml || undefined}
            signatures={extras.signatures}
            activeSignatureId={extras.activeSignatureId}
            onPickSignature={extras.onPickSignature}
          />
        </div>
      );
    case "send_cold_email":
      // Outbound prospecting cold email (Jun 2026). Reuses the SAME envelope
      // editor as send_email — DRY. ``to`` is a single string here (vs the
      // list for send_email); ``RecipientsInput`` coerces via ``asStringArray``
      // so the single recipient renders as one chip. The GDPR opt-out footer is
      // injected + re-checked server-side, so it's intentionally not an editable
      // field. Stacked editor suppressed via emailOwnedFields(schema) below.
      return (ctx: PreviewControlledCtx) => (
        <div className="space-y-3">
          <EmailTemplatePicker
            actionType="send_cold_email"
            values={ctx.values}
            onChange={ctx.onChange}
            disabled={ctx.disabled}
          />
          <EmailSendDraftPreview
            proposalId={action.id}
            refineSubmit={extras.refineSubmit}
            refineSubmitWithDiff={extras.refineSubmitWithDiff}
            proposedFields={proposed}
            executedFields={executed}
            schema={ctx.schema}
            values={ctx.values}
            errors={ctx.errors}
            onChange={ctx.onChange}
            disabled={ctx.disabled}
            loadPeopleOptions={extras.loadPeopleOptions}
            uploadAttachment={extras.uploadAttachment}
            searchAttachableDocuments={extras.searchAttachableDocuments}
            brandFonts={extras.brandFonts}
          />
        </div>
      );
    case "send_message":
      // Same envelope pattern as send_email — controlled inline editor +
      // suppress the stacked ActionFieldEditor for ``channel_name`` /
      // ``message`` via ``previewOwnedFields`` below. The body textarea
      // is wrapped in InlineAIRefiner so substring selection surfaces the
      // floating AI rewriter (May 26, 2026 UX parity rollout).
      return (ctx: PreviewControlledCtx) => (
        <SendMessagePreview
          proposalId={action.id}
          refineSubmit={extras.refineSubmit}
          proposedFields={proposed}
          executedFields={executed}
          schema={ctx.schema}
          values={ctx.values}
          errors={ctx.errors}
          onChange={ctx.onChange}
          disabled={ctx.disabled}
        />
      );
    case "calendar_create_event":
      // Render-prop (NOT a static node) so the event is edited INLINE on the
      // preview: title / start / end / location / attendees / notes bind to the
      // drawer's live ``values`` + ``onChange``. ``CALENDAR_PREVIEW_OWNED_FIELDS``
      // (below) drops those from the stacked editor → one card, no duplication,
      // and the preview updates as you type (was frozen to a store snapshot).
      return (ctx: PreviewControlledCtx) => (
        <CalendarCreateEventPreview
          proposedFields={proposed}
          executedFields={executed}
          schema={ctx.schema}
          values={ctx.values}
          errors={ctx.errors}
          onChange={ctx.onChange}
          disabled={ctx.disabled}
          loadPeopleOptions={extras.loadPeopleOptions}
        />
      );
    case "fic_create_invoice":
      return <FicCreateInvoicePreview proposedFields={proposed} executedFields={executed} />;
    case "fic_credit_note":
      return <FicCreditNotePreview proposedFields={proposed} executedFields={executed} />;
    case "fic_payment":
      // Controlled render-prop: the editor owns payment_date / account /
      // notes inline; the stacked editor is suppressed via
      // FIC_PAYMENT_PREVIEW_OWNED_FIELDS below.
      return (ctx: PreviewControlledCtx) => (
        <FicPaymentPreview
          proposedFields={proposed}
          executedFields={executed}
          schema={ctx.schema}
          values={ctx.values}
          errors={ctx.errors}
          onChange={ctx.onChange}
          disabled={ctx.disabled}
        />
      );
    case "initiate_sepa_payment":
      // Controlled render-prop: the SEPA composer owns its full surface
      // (amount hero + beneficiary IBAN block) and binds to the live
      // ActionDrawer values; the stacked editor is suppressed via
      // SEPA_PREVIEW_OWNED_FIELDS below.
      return (ctx: PreviewControlledCtx) => (
        <SepaPaymentPreview
          proposedFields={proposed}
          executedFields={executed}
          schema={ctx.schema}
          values={ctx.values}
          errors={ctx.errors}
          onChange={ctx.onChange}
          disabled={ctx.disabled}
        />
      );
    case "aruba_pec_send":
      // Controlled envelope editor (mirrors send_email) — owns provider /
      // from_pec_address / to / cc / subject / body inline; stacked editor
      // suppressed via PEC_PREVIEW_OWNED_FIELDS below.
      return (ctx: PreviewControlledCtx) => (
        <PecSendPreview
          proposalId={action.id}
          refineSubmit={extras.refineSubmit}
          proposedFields={proposed}
          executedFields={executed}
          providerLabel="Aruba PEC"
          schema={ctx.schema}
          values={ctx.values}
          errors={ctx.errors}
          onChange={ctx.onChange}
          disabled={ctx.disabled}
        />
      );
    case "infocert_pec_send":
      return (ctx: PreviewControlledCtx) => (
        <PecSendPreview
          proposalId={action.id}
          refineSubmit={extras.refineSubmit}
          proposedFields={proposed}
          executedFields={executed}
          providerLabel="InfoCert PEC"
          schema={ctx.schema}
          values={ctx.values}
          errors={ctx.errors}
          onChange={ctx.onChange}
          disabled={ctx.disabled}
        />
      );
    case "submit_invoice_sdi":
      return <SdiInvoicePreview proposedFields={proposed} executedFields={executed} />;
    case "document_edit":
    case "document_fork_edit":
      return <DocumentForkPreview proposedFields={proposed} executedFields={executed} />;
    case "document_create": {
      // Read the dedicated ``documentCreate`` blob lifted off the wire
      // by ``useActionRouter`` — ``proposed_fields`` is empty for create
      // proposals; the BE ships the human-readable summary alongside
      // ``ActionProposal`` as a sibling field.
      if (!action.documentCreate) return null;
      const destState = extras.documentCreateDestination;
      const destinationProps = destState
        ? {
            value: destState.value,
            onChange: destState.setValue,
            folders: destState.folders,
            isLoadingFolders: destState.isLoadingFolders,
            onOpenIntegrations: extras.onOpenIntegrations,
            disabled: extras.disabled,
          }
        : undefined;
      // Presentation proposals with a ``slide_list`` field get the per-slide
      // editor; everything else (Word/xlsx/pdf, legacy decks) keeps the
      // read-only markdown outline below.
      const styleTemplateState = extras.documentCreateStyleTemplate;
      // Shared mapping (single source of truth) — same helper the editor
      // DraftActionBar uses, so card ↔ editor pickers can't drift.
      const styleTemplateProps = styleTemplateState
        ? toStyleTemplatePickerProps(styleTemplateState, {
            disabled: extras.disabled,
            onOpenTemplateSettings: extras.onOpenTemplateSettings,
          })
        : undefined;
      const slidesState = extras.documentCreateSlides;
      const slideOutlineProps = slidesState?.hasSlideList
        ? {
            slides: slidesState.slides,
            layoutOptions: slidesState.layoutOptions,
            onSlidesChange: slidesState.setSlides,
            title: slidesState.title,
            onTitleChange: slidesState.setTitle,
            disabled: extras.disabled,
          }
        : undefined;
      const previewsState = extras.documentCreateSlidePreviews;
      // Only when there's no editable outline: a sandbox deck's rendered
      // thumbnails (or the in-flight render skeleton) replace the text summary.
      const slidePreviewsProps =
        !slideOutlineProps &&
        previewsState &&
        (previewsState.slides.length > 0 || previewsState.isLoading)
          ? {
              images: previewsState.slides.map((s) => ({
                index: s.index,
                url: s.url,
              })),
              loading: previewsState.isLoading,
            }
          : undefined;
      return (
        <DocumentCreatePreview
          title={action.documentCreate.title}
          documentType={action.documentCreate.documentType}
          contentPreview={action.documentCreate.contentPreview}
          availableDestinations={action.documentCreate.availableDestinations}
          destination={destinationProps}
          styleTemplate={styleTemplateProps}
          slideOutline={slideOutlineProps}
          slidePreviews={slidePreviewsProps}
          onRefineSection={async ({ sectionHeading, instruction }) => {
            try {
              const res = await refineDocumentSection(action.id, {
                section_heading: sectionHeading,
                instruction,
              });
              if (!res.success || !res.content_preview) {
                toast.error(
                  res.message ??
                    extras.refineSectionLabels?.failed ??
                    "Impossibile rigenerare la sezione",
                );
                return;
              }
              // Patch the action store in place — the proposal id is
              // unchanged, so the FocusedActionView re-renders with
              // the refreshed preview (audit lineage preserved on the
              // BE side, see `refine_document_create_section`).
              if (action.documentCreate) {
                activeActionApi.patch(action.id, {
                  documentCreate: {
                    ...action.documentCreate,
                    contentPreview: res.content_preview,
                  },
                });
              }
              toast.success(extras.refineSectionLabels?.success ?? "Sezione rigenerata");
            } catch (err) {
              const msg = err instanceof Error ? err.message : "Errore";
              toast.error(
                extras.refineSectionLabels?.error
                  ? extras.refineSectionLabels.error(msg)
                  : `Impossibile rigenerare: ${msg}`,
              );
            }
          }}
        />
      );
    }
    case "generate_quotation":
      // Jul 10, 2026, issue #903 — Draft customer quotation from a CAD
      // BOM x tenant price-list match. Read-only card: the "no invented
      // prices" invariant of the pipeline means editing the amount here
      // would defeat the point; the reviewer either accepts what
      // actually matched or rejects the draft and manually quotes.
      return <QuotationDraftPreview proposedFields={proposed} executedFields={executed} />;
    default:
      // Generic fallback — pretty-print proposed_fields as a 2-column
      // dl inside the shared VendorPreviewShell. Covers the 19 action
      // types without a dedicated preview (task_create, salesforce_*,
      // hubspot_*, sap_*, acube_register_cassetto/archive/peppol,
      // infocert_pec_send, fic_schedule_invoice_email, …) so the
      // focused canvas is never empty — every proposal renders SOMETHING
      // the user can read + confirm. May 25, 2026 SOTA pass.
      return (
        <GenericProposalPreview
          title={action.title}
          actionType={action.actionType}
          proposedFields={proposed}
          executedFields={executed}
        />
      );
  }
}

export interface FocusedActionViewProps {
  /** The proposal being displayed. */
  readonly action: ActiveAction;
  /** Tab label for the eyebrow ("Conversazioni", "Calendario", …). */
  readonly tabLabel: string;
  /** ``?msg=`` from the URL — propagates to the SourceRail. */
  readonly messageId: string | null;
  /** Back-chevron handler: clears ``?proposal=`` from the URL. */
  readonly onBack: () => void;
}

/**
 * The view component itself — pure presentation + handler wiring.
 * Owns the bidirectional chip ↔ drawer sync for document-class
 * proposals (same logic as ``ActionFormSurface::ActionRow`` — kept
 * verbatim to avoid divergence).
 */
export const FocusedActionView: React.FC<FocusedActionViewProps> = ({
  action,
  tabLabel,
  messageId,
  onBack,
}) => {
  const { t } = useLingui();
  const markChipDecision = useProposalsStore((s) => s.markDecision);
  const isDocAction = DOC_ACTION_TYPES.has(action.actionType);
  const payloadSchemaQuery = usePayloadSchema(action.id);
  const schema = React.useMemo(() => {
    // Wire-shipped schema wins when non-empty (preview-synthesised types
    // like ``send_email`` may have already shipped a lite version on the
    // WS frame). Otherwise use the lazily-fetched unified schema.
    if (action.schema.length > 0) return action.schema;
    return payloadSchemaQuery.editorSchema;
  }, [action.schema, payloadSchemaQuery.editorSchema]);
  // Firma — resolved for the CURRENTLY SELECTED "Da" mailbox so changing the
  // from-account picker re-resolves the signature (per-account default → user →
  // org). ``liveFromAccount`` tracks the drawer's live ``from_account`` value
  // (seeded from the edited/proposed fields, updated via ``onValuesChange``).
  const seededFromAccount = React.useMemo(() => {
    const src = (action.editedFields ?? action.proposedFields) as Record<
      string,
      ActionFieldValue
    >;
    return typeof src.from_account === "string" && src.from_account
      ? src.from_account
      : undefined;
  }, [action.editedFields, action.proposedFields]);
  const [liveFromAccount, setLiveFromAccount] = React.useState<string | undefined>(
    seededFromAccount,
  );
  React.useEffect(() => {
    setLiveFromAccount(seededFromAccount);
  }, [seededFromAccount]);
  const sender = resolveEmailSender(schema.find((field) => field.name === "from_account")?.options ?? [], liveFromAccount);
  const effectiveSigQ = useEffectiveSignature(sender.realEmail, !sender.sendOnly && (!liveFromAccount || Boolean(sender.realEmail)));
  // Pool of named signatures the operator can pick between for THIS email.
  const signaturePoolQ = useSignatures();
  const signatureOptions = React.useMemo(
    () => (signaturePoolQ.data ?? []).map((s) => ({ id: s.id, name: s.name })),
    [signaturePoolQ.data],
  );
  // Manual pick overrides the account-resolved default; reset on account change.
  const [pickedSignatureId, setPickedSignatureId] = React.useState<string | null>(
    null,
  );
  React.useEffect(() => {
    setPickedSignatureId(null);
  }, [liveFromAccount]);
  const activeSignatureId =
    pickedSignatureId ?? effectiveSigQ.data?.signature_id ?? null;
  const effectiveSignatureHtml = React.useMemo(() => {
    const picked =
      pickedSignatureId != null
        ? signaturePoolQ.data?.find((s) => s.id === pickedSignatureId)
        : undefined;
    return sanitizeHtml(
      picked?.signature_html ?? effectiveSigQ.data?.effective_html ?? "",
    );
  }, [pickedSignatureId, signaturePoolQ.data, effectiveSigQ.data?.effective_html]);
  // Fires on every field edit inside the drawer — keeps ``liveFromAccount`` in
  // sync with the Da picker so the signature re-resolves as the user switches.
  const handleValuesChange = React.useCallback(
    (next: Record<string, ActionFieldValue>) => {
      const acct =
        typeof next.from_account === "string" && next.from_account
          ? next.from_account
          : undefined;
      setLiveFromAccount(acct);
    },
    [],
  );
  // Brand Kit fonts for the email body editor's font dropdown (+ FontFace load).
  const brandFonts = useBrandFonts();

  const isDocCreate = action.actionType === "document_create";
  // Only the email envelopes re-resolve the signature per selected FROM account,
  // so the live-values callback is wired for those types alone (avoids an extra
  // re-render per keystroke on every other action form).
  const isEmailType =
    action.actionType === "send_email" ||
    action.actionType === "email_send_draft" ||
    action.actionType === "send_cold_email";
  const { confirmWithUndo, cancel, pending, busy: executing } = useConfirmWithUndo();
  const navigate = useNavigate();

  // Ref-stable transports threaded into ``<ActionDrawer>`` so the inner
  // ``<ActionFieldEditor>`` (and the email envelope ``preview``) can
  // power their chip+autocomplete + file picker without recreating
  // closures on every render. See ``useActionEditorTransports``.
  const loadPeopleOptions = useLoadPeopleOptions();
  const uploadAttachment = useUploadAttachment();
  const searchAttachableDocuments = useSearchAttachableDocuments();

  // Single submit transport for the inline AI refiner — wired here so
  // every preview that opts in (email body, message body, …) goes
  // through the same audited POST /actions/{id}/refine-field path.
  // Stable identity via useCallback so the InlineAIRefiner doesn't
  // re-mount on every keystroke.
  const refineSubmit = React.useCallback(async (input: import("@ando/ui").RefineSubmitInput) => {
    const res = await refineField(input.proposalId, {
      field_name: input.fieldName,
      full_text: input.fullText,
      selection_start: input.selectionStart,
      selection_end: input.selectionEnd,
      instruction: input.instruction,
    });
    return {
      success: res.success,
      newFullText: res.new_full_text ?? null,
      message: res.message ?? null,
    };
  }, []);

  // Diff-mode transport (Jul 2026) — same audited POST /actions/{id}/refine-field
  // path, but returns the SELECTED substring before/after + the word-level
  // ``changes`` so InlineAIRefiner renders the Apple-style inline redline before
  // committing. ``original`` MUST equal the selected substring so the primitive
  // splices the reconstructed text back over the selection (see applyDiff).
  const refineSubmitWithDiff = React.useCallback<import("@ando/ui").RefineSubmitWithDiff>(
    async (input) => {
      const res = await refineField(input.proposalId, {
        field_name: input.fieldName,
        full_text: input.fullText,
        selection_start: input.selectionStart,
        selection_end: input.selectionEnd,
        instruction: input.instruction,
      });
      if (!res.success) {
        throw new Error(res.message ?? t`Impossibile rigenerare la selezione.`);
      }
      const selected = input.fullText.slice(input.selectionStart, input.selectionEnd);
      return {
        original: res.original ?? selected,
        rewritten: res.new_text ?? selected,
        changes: res.changes ?? [],
      };
    },
    [t],
  );

  // Destination + folder picker for ``document_create`` proposals.
  // The hook is called unconditionally (rules-of-hooks) but no-ops
  // for non-document_create types: passing an empty
  // ``availableDestinations`` seeds it to ``minio`` and the underlying
  // ``useDriveFolders`` is gated by ``enabled: provider !== undefined``,
  // so non-cloud destinations skip the network entirely. May 2026 SOTA:
  // closes the loop end-to-end for "salva nella cartella Drive: <URL>".
  const destinationState = useDocumentCreateDestination({
    availableDestinations: action.documentCreate?.availableDestinations ?? [],
  });

  // Style / template choice for ``document_create`` proposals — the picker
  // fused into the card so the user chooses HOW the file is created before
  // Conferma. Called unconditionally (rules-of-hooks); the underlying
  // queries stay cheap (5-min staleTime, shared cache) and the result is
  // only threaded into the preview + confirm path for document_create.
  // Gate the 3 style/template fetches to document_create only (this hook is
  // called unconditionally per rules-of-hooks). Sandbox-authored proposals
  // (``sandboxAuthored``) skip the picker entirely: the precomputed bytes ARE
  // the document — offering a style/template choice there was a lie (the BE
  // ignores the pick to preserve WYSIWYG; Jul 6, 2026).
  const styleTemplateState = useDocumentCreateStyleTemplate(isDocCreate && !action.sandboxAuthored);

  // Fetch the canonical editable schema from the BE
  // (``GET /api/v1/actions/{id}/payload-schema``). The WS frame ships
  // only ``proposed_fields`` for performance — the unified schema with
  // ``to / cc / bcc / subject / body`` editor specs (or per-vendor
  // fields for SF/FiC/Acube/PEC) arrives via this lazy fetch on mount.
  // Without it the ``ActionDrawer`` ``ActionFieldEditor`` renders zero
  // inputs and the user sees a READ-ONLY mock (May 25, 2026 user
  // report: "in v1 avevo la possibilità di modificare anche la mail
  // e il from"). v1 parity restored by merging the fetched schema
  // into ``ActionDrawer.schema``.


  // Per-slide editable outline for presentation ``document_create`` proposals.
  // Called unconditionally (rules-of-hooks); ``hasSlideList`` stays false for
  // every other proposal (no ``slide_list`` field on their schema), so the
  // preview falls back to the read-only markdown outline and
  // ``buildModifications()`` returns null at confirm time.
  const slidesState = useSlideOutlineDraft({
    proposalId: action.id,
    outline: payloadSchemaQuery.slideOutline,
  });

  // WYSIWYG thumbnails for a SANDBOX-authored deck (finished binary, no
  // editable outline). Enabled only for sandbox presentations — every other
  // proposal returns an empty list from the BE, so gating avoids a pointless
  // render request. When the editable ``slide_list`` outline exists we prefer
  // it (the deck came from the OfficeCLI outline path, not the sandbox).
  const slidePreviewsState = useSlidePreviews(
    action.id,
    isDocCreate &&
      action.documentCreate?.documentType === "presentation" &&
      Boolean(action.sandboxAuthored) &&
      !slidesState.hasSlideList,
  );

  const handleReject = React.useCallback(() => {
    // Also cancels a pending send (undo window) before dismissing.
    cancel();
    if (isDocAction) markChipDecision(action.id, "rejected");
    activeActionApi.remove(action.id);
    // Pop the URL param so the work area returns to the hub list. If we
    // skip this, the param lingers and FocusedActionSurface re-renders
    // an empty state until the next nav.
    onBack();
  }, [action.id, cancel, isDocAction, markChipDecision, onBack]);

  const handleReconnect = React.useCallback(() => {
    void navigate({ to: "/integrations" });
  }, [navigate]);

  const handleSaveDraft = React.useCallback(
    (edited: Record<string, ActionFieldValue>) => {
      activeActionApi.patch(action.id, { editedFields: edited });
      toast.success(t`Bozza salvata`);
    },
    [action.id, t],
  );

  const handleConfirm = React.useCallback(
    (
      executed: Record<string, ActionFieldValue>,
      extraModifications?: Record<string, unknown>,
    ) => {
      if (isDocAction) markChipDecision(action.id, "accepted");
      const proposed = action.proposedFields;
      // ``unknown`` values: scalar field edits land here, but presentation
      // ``document_create`` also commits a structured ``slides`` array. The
      // execute mutation already accepts ``Record<string, unknown>``.
      // ``extraModifications`` (e.g. { confirm_source: true } from the Tier-2
      // gate recovery) seed the map before the diff is layered on.
      const modifications: Record<string, unknown> = { ...extraModifications };
      for (const [k, v] of Object.entries(executed)) {
        if (proposed[k] !== v) modifications[k] = v;
      }
      // For ``document_create``, merge the destination + folder choice
      // into modifications so the BE routes via the resolution chain
      // (modifications > preview.save_destination > default minio).
      // Use ``Object.assign`` to keep field-name parity with the BE
      // wire — keys are emitted as the user picked them, which is what
      // the audit-log diff stores. Non-cloud destinations naturally
      // omit ``folder_id`` thanks to ``buildModifications()`` logic.
      if (isDocCreate) {
        Object.assign(modifications, destinationState.buildModifications());
        // Style / template choice → ``_style_id`` / ``_ooxml_template_slug``
        // content meta-keys. The BE merges ``modifications`` into the action
        // content (same path as ``save_destination``); Ando default emits
        // nothing so the BE applies its own default look. Sandbox-authored
        // proposals never emit a pick (picker hidden — bytes are the doc).
        if (!action.sandboxAuthored) {
          Object.assign(modifications, styleTemplateState.buildModifications());
        }
        // Presentation proposals additionally commit the edited
        // ``{ title, slides }`` — the BE reads ``modifications.slides`` to
        // regenerate the Marp markdown + re-render the deck. ``null`` for
        // non-presentation document_create (no slide_list), so the keys are
        // simply absent and the BE keeps its original outline.
        const slideMods = slidesState.buildModifications();
        if (slideMods) {
          Object.assign(modifications, slideMods);
        }
      }
      // The generic executing→executed|error patch lifecycle (+ persisting
      // ``editedFields`` on the optimistic flip, + the SOT result_data →
      // executedData/receipt stash + error-kind classification) lives in
      // ``useProposalExecution``. Confirm defers the real execute by the undo
      // window (Feature 2) — EXCEPT for document actions, which are reversible
      // (the user reviews the generated/edited doc) and route through the
      // Temporal async path, so they commit immediately.
      confirmWithUndo(
        action.id,
        Object.keys(modifications).length > 0 ? modifications : undefined,
        {
          immediate: isDocAction || isDocCreate,
          editedFields: executed,
          onUndo: () => {
            toast.info(t`Invio annullato`);
          },
          onSuccess: (res) => {
            // Auto-open the JUST-CREATED file in the work area (June 2026
            // user req: "appena è completato mi si deve aprire SUBITO in
            // automatico il file creato"). Two cases produce a NEW doc at
            // execute time:
            //   1. ``document_create`` — the doc is materialised on execute.
            //   2. a DEFERRED FORK (a ``document_edit`` that saves a NEW copy) —
            //      the fork is created on accept; its ``weaviate_doc_id`` +
            //      ``is_fork`` arrive here in ``result_data``.
            // Open the new doc for BOTH. A plain in-place edit carries no
            // ``is_fork`` and no new doc → skip (stay on the edited doc).
            const data = (res?.result_data ?? {}) as {
              doc_id?: unknown;
              is_fork?: unknown;
            };
            const isForkSave = data.is_fork === true;
            if (isDocCreate || isForkSave) {
              const newDocId = typeof data.doc_id === "string" ? data.doc_id : null;
              if (newDocId) {
                const def = getWorkspaceTabDef("documents");
                if (def) {
                  void navigate({ to: def.route, search: { focus: newDocId } });
                }
              }
              return;
            }
            // Every other action (calendar event, email send, CRM deal, …):
            // show a clear success banner and CLOSE the focused view — don't
            // leave the user on a stale "Executed" form (Jul 2026 user req).
            toast.success(t`Azione eseguita`, {
              description: typeof action.title === "string" ? action.title : undefined,
            });
            onBack();
          },
          onError: (err) => {
            if (isDocAction) {
              useProposalsStore.setState((prev) => {
                const next: Record<string, ReadonlyArray<PendingProposal>> = {};
                for (const [docId, items] of Object.entries(prev.byDoc)) {
                  next[docId] = items.map((p) =>
                    p.proposal_id === action.id ? { ...p, decision: null } : p,
                  );
                }
                return { byDoc: next };
              });
            }
            // Classified failures render an INLINE recovery on the card
            // (ActionErrorRecovery, Feature 4) — no toast for those. Only an
            // unclassified error (no ``kind``) falls back to a toast.
            const kind = (err as Error & { kind?: string }).kind;
            if (!kind) {
              toast.error(err.message ?? t`Esecuzione azione fallita. Riprova.`);
            }
          },
        },
      );
    },
    [
      action.id,
      action.proposedFields,
      action.title,
      action.sandboxAuthored,
      confirmWithUndo,
      isDocAction,
      isDocCreate,
      markChipDecision,
      navigate,
      onBack,
      t,
      // Depend on the stable ``buildModifications`` callback (memoized
      // inside the hook on ``[value]``) instead of the whole
      // ``destinationState`` object — the latter is a fresh literal
      // each render which would force ``handleConfirm`` (and downstream
      // ``ActionDrawer``) to re-render on every keystroke. May 2026 SOTA:
      // narrow deps to the actual usage.
      destinationState.buildModifications,
      // Same narrowing rationale — the slide ``buildModifications`` callback
      // is memoized on ``[title, slides]`` inside the hook.
      slidesState.buildModifications,
      // Memoized on ``[value]`` inside the style/template hook.
      styleTemplateState.buildModifications,
    ],
  );

  // Eyebrow suffix — vendor label is the closest analogue to "GMAIL"
  // / "TRA 30 MINUTI" used by the inbox/calendar focused views. When
  // the BE doesn't ship a vendor label, fall back to the action_type
  // category from the icon mapping (already coarse-grained per
  // ``utils/actionTypeIcon.tsx``).
  const eyebrowSuffix = action.vendorLabel ?? undefined;

  // The pending undo window reads as "executing" to the drawer so its controls
  // lock while the countdown runs (nothing has been sent yet).
  const inFlight = executing || pending !== null;
  const status: ActionDrawerStatus = inFlight ? "executing" : action.status;

  // ── Feature 3/4: receipt + inline recovery banners for the focused card ──
  const receiptNode = React.useMemo(() => {
    if (action.status !== "executed") return null;
    const data = (action.executedData ?? {}) as {
      result_url?: unknown;
      result_label?: unknown;
      external_id?: unknown;
    };
    const url = typeof data.result_url === "string" ? data.result_url : null;
    const externalId = typeof data.external_id === "string" ? data.external_id : null;
    if (!url && !externalId) return null;
    return (
      <ActionReceipt
        url={url}
        label={typeof data.result_label === "string" ? data.result_label : null}
        externalId={externalId}
        className="rounded-md border border-hairline"
      />
    );
  }, [action.status, action.executedData]);

  const errorNode = React.useMemo(() => {
    if (!action.errorKind) return null;
    if (action.status === "executing" || action.status === "executed") return null;
    const meta = (action.errorMeta ?? {}) as { account_label?: unknown };
    const sourceValues = readSourceValues(action.errorMeta);
    const lastFields = (action.editedFields ?? action.proposedFields) as Record<
      string,
      ActionFieldValue
    >;
    return (
      <ActionErrorRecovery
        kind={action.errorKind as ActionErrorKind}
        message={action.error ?? undefined}
        accountLabel={typeof meta.account_label === "string" ? meta.account_label : undefined}
        sourceValues={sourceValues}
        busy={inFlight}
        onReconnect={handleReconnect}
        // Retry re-opens the drawer's confirm path: re-run with the last
        // edited fields (or the proposed set if none were saved).
        onRetry={() => handleConfirm(lastFields)}
        // Tier-2 gate: confirm the value against the source of record.
        onConfirmSource={() => handleConfirm(lastFields, { confirm_source: true })}
      />
    );
  }, [
    action.errorKind,
    action.errorMeta,
    action.error,
    action.status,
    action.editedFields,
    action.proposedFields,
    inFlight,
    handleConfirm,
    handleReconnect,
  ]);

  // ⌘/Ctrl+Enter → Confirm, scoped to the focused card (the handler sits on the
  // pane wrapper, so it only fires when focus is inside). Esc→dismiss is already
  // provided by the drawer's ``focusedModal``. Confirm here commits the CURRENT
  // edited fields (or the proposed set).
  const handlePaneKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        if (inFlight || action.status === "executed") return;
        e.preventDefault();
        handleConfirm(
          (action.editedFields ?? action.proposedFields) as Record<string, ActionFieldValue>,
        );
      }
    },
    [inFlight, action.status, action.editedFields, action.proposedFields, handleConfirm],
  );

  // While a document_create is generating (synchronous, can be ~2 min for an
  // image-heavy deck), take over the working area with the Ando blob loader
  // instead of leaving the now-disabled proposal form on screen — the brand
  // wait-beat, and clear feedback that creation is in flight. The preview opens
  // by itself when generation finishes (execute onSuccess → auto-open).
  const isCreatingDoc = isDocCreate && (executing || action.status === "executing");

  // Safety net: the async (Temporal) path opens the doc via the WS
  // ``action_status: completed`` frame. If that frame is lost (Redis hiccup) the
  // blob loader would otherwise spin forever. After a generous ceiling (longer
  // than any real generation) surface a graceful fallback so the user can recover.
  const [creationStalled, setCreationStalled] = React.useState(false);
  React.useEffect(() => {
    if (!isCreatingDoc) {
      setCreationStalled(false);
      return;
    }
    const t = setTimeout(() => setCreationStalled(true), 6 * 60 * 1000);
    return () => clearTimeout(t);
  }, [isCreatingDoc]);

  const headerActions = <ActionStatusBadge status={status} />;

  return (
    <FocusedViewLayout
      tabLabel={tabLabel}
      eyebrowSuffix={eyebrowSuffix}
      title={action.title}
      subtitle={action.subtitle ? <span className="truncate">{action.subtitle}</span> : undefined}
      icon={renderActionTypeIcon(action.actionType)}
      backAriaLabel={t`Torna all'elenco`}
      onBack={onBack}
      messageId={messageId}
      focusId={action.id}
      headerActions={headerActions}
    >
      <PaneBody className="space-y-4 px-4 py-4" onKeyDown={handlePaneKeyDown}>
        {/* Feature 2 — pre-execute undo window (non-document sends only). */}
        {pending ? (
          <ActionUndoBar
            secondsLeft={pending.secondsLeft}
            totalSeconds={pending.totalSeconds}
            onCancel={cancel}
            actionNoun={t`Invio`}
            className="rounded-md border border-hairline bg-white/50 px-3 py-2"
          />
        ) : null}
        {/* Feature 3 — post-execute receipt. */}
        {receiptNode}
        {/* Feature 4 — inline error recovery (replaces the generic toast). */}
        {errorNode}
        {isCreatingDoc ? (
          <div className="flex min-h-[420px] flex-col items-center justify-center gap-5 py-16 text-center">
            <AndoBrainLoader size="lg" label={t`Sto creando il documento…`} />
            {creationStalled ? (
              <p className="max-w-[46ch] text-[12.5px] text-ink-3 leading-snug">
                <Trans>
                  Sta impiegando più del previsto. Il documento potrebbe essere comunque pronto in{" "}
                  <strong>Documenti</strong> — ricarica la pagina se non si apre da solo a breve.
                </Trans>
              </p>
            ) : (
              <p className="max-w-[44ch] text-[12.5px] text-ink-3 leading-snug">
                <Trans>
                  Genero contenuti, layout e — per i deck — le illustrazioni su misura. Può volerci
                  qualche decina di secondi; l'anteprima si apre da sola appena è pronta.
                </Trans>
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Whole-form AI assist — like the email composer, the user can fill
            the form by hand OR get the AI to draft it from an instruction.
            Hidden for doc-class proposals (they have their own per-section
            refine flow). Per-field rewriting is handled by InlineAIRefiner
            inside the previews. */}
            {!isDocAction && !isDocCreate && status !== "executed" ? (
              <ActionAiFillBar
                proposalId={action.id}
                disabled={executing}
                onFilled={(fields) =>
                  activeActionApi.patch(action.id, {
                    proposedFields: { ...action.proposedFields, ...fields },
                  })
                }
              />
            ) : null}
            <ActionDrawer
              hideHeader
              unstyled
              // a11y: the FocusedViewLayout is a take-over pane; we surface the
              // drawer as a true dialog with autofocus + Escape→back so screen
              // readers and keyboard-only users get parity with mouse users.
              focusedModal
              onClose={onBack}
              // Title/subtitle are surfaced by the FocusedViewLayout chrome
              // above — ActionDrawer still requires them on the type, so we
              // pass the same values for backwards compatibility (they're
              // simply not rendered when ``hideHeader=true``).
              title={action.title}
              subtitle={action.vendorLabel ?? action.subtitle}
              schema={sender.sendOnly ? schema.map((field) => ["scheduled_for", "send_at", "provider", "in_reply_to_message_id", "reply_to_message_id", "forward_message_id", "thread_id"].includes(field.name) ? { ...field, readonly: true } : field) : schema}
              proposedFields={action.proposedFields}
              initialEditedFields={action.editedFields}
              status={status}
              error={action.error}
              reasoning={action.reasoning}
              citations={action.citations}
              preview={pickPreview(action, {
                refineSectionLabels: {
                  failed: t`Impossibile rigenerare la sezione`,
                  success: t`Sezione rigenerata`,
                  error: (detail) => t`Impossibile rigenerare: ${detail}`,
                },
                documentCreateDestination: isDocCreate ? destinationState : undefined,
                documentCreateSlides: isDocCreate ? slidesState : undefined,
                documentCreateSlidePreviews: isDocCreate ? slidePreviewsState : undefined,
                documentCreateStyleTemplate:
                  isDocCreate && !action.sandboxAuthored ? styleTemplateState : undefined,
                signatureHtml: sender.sendOnly ? undefined : effectiveSignatureHtml,
                signatures: sender.sendOnly ? [] : signatureOptions,
                activeSignatureId,
                onPickSignature: setPickedSignatureId,
                brandFonts,
                disabled: executing,
                onOpenIntegrations: () => void navigate({ to: "/integrations" }),
                onOpenTemplateSettings: () => void navigate({ to: "/settings/marp-templates" }),
                refineSubmit,
                refineSubmitWithDiff,
                loadPeopleOptions,
                uploadAttachment,
                searchAttachableDocuments,
              })}
              previewOwnedFields={
                action.actionType === "send_email" ||
                action.actionType === "email_send_draft" ||
                action.actionType === "send_cold_email"
                  ? emailOwnedFields(schema)
                  : action.actionType === "send_message"
                    ? MESSAGE_PREVIEW_OWNED_FIELDS
                    : action.actionType === "document_create"
                      ? DOCUMENT_CREATE_PREVIEW_OWNED_FIELDS
                      : action.actionType === "initiate_sepa_payment"
                        ? SEPA_PREVIEW_OWNED_FIELDS
                        : action.actionType === "aruba_pec_send" ||
                            action.actionType === "infocert_pec_send"
                          ? PEC_PREVIEW_OWNED_FIELDS
                          : action.actionType === "fic_payment"
                            ? FIC_PAYMENT_PREVIEW_OWNED_FIELDS
                            : canonicalActionType(action.actionType) ===
                                "calendar_create_event"
                              ? CALENDAR_PREVIEW_OWNED_FIELDS
                              : undefined
              }
              onValuesChange={isEmailType ? handleValuesChange : undefined}
              onReject={handleReject}
              onSaveDraft={handleSaveDraft}
              onConfirm={handleConfirm}
              loadPeopleOptions={loadPeopleOptions}
              uploadAttachment={uploadAttachment}
              searchAttachableDocuments={searchAttachableDocuments}
              confirmPhrase={
                DESTRUCTIVE_ACTION_TYPES.has(action.actionType)
                  ? "ELIMINA"
                  : action.actionType === "initiate_sepa_payment"
                    ? "CONFERMA"
                    : undefined
              }
            />
          </>
        )}
      </PaneBody>
    </FocusedViewLayout>
  );
};
FocusedActionView.displayName = "FocusedActionView";
